import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { cn } from '@tsgecart/ui';

export interface AdminMetricTrendProps {
  /** `null` means "no prior-period baseline to compare against" -- rendered as a neutral dash, never a fabricated 0%. */
  trendPercent: number | null;
  className?: string;
}

/**
 * Never color-only (Part 16 "no color-only status indicators"): every
 * state pairs an icon with visible text, so the meaning survives for a
 * colorblind viewer or a screen reader (the icon is `aria-hidden`, the
 * text carries the actual information).
 */
export function AdminMetricTrend({ trendPercent, className }: AdminMetricTrendProps) {
  if (trendPercent === null) {
    return (
      <span className={cn('inline-flex items-center gap-1 text-sm text-gray-400', className)}>
        <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        No prior data
      </span>
    );
  }

  const isFlat = trendPercent === 0;
  const isUp = trendPercent > 0;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-sm font-medium',
        isFlat && 'text-gray-500',
        !isFlat && isUp && 'text-green-700',
        !isFlat && !isUp && 'text-red-700',
        className,
      )}
    >
      {isFlat ? (
        <Minus className="h-3.5 w-3.5" aria-hidden="true" />
      ) : isUp ? (
        <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {isFlat ? 'No change' : `${Math.abs(trendPercent).toFixed(1)}% ${isUp ? 'up' : 'down'}`}
      <span className="sr-only"> vs. previous period</span>
    </span>
  );
}
